/**
 * useDesktopLayout — one person's Desktop arrangement for one workspace,
 * loaded from the server and saved back through `DesktopLayoutSaver`
 * (debounced, coalesced, one request in flight, retried on failure).
 *
 * The saver is created per holder and captures it, so switching workspace
 * can never send one workspace's arrangement to another; unmounting (which a
 * workspace switch does — the Desktop is keyed by holder), hiding the tab and
 * leaving the page all flush what's pending.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { DesktopLayoutSaver, type LayoutSaveStatus } from '../lib/desktop-layout-saver';
import { normalizeLayout, type DesktopLayout, type LayoutPatch } from '../lib/desktop-layout';

export interface DesktopLayoutController {
  /** The server's layout with every unsaved local change drawn on top. */
  layout: DesktopLayout;
  status: LayoutSaveStatus;
  /** The first load has finished (or failed — a failed load draws the
   *  default arrangement and still saves changes). */
  ready: boolean;
  loadFailed: boolean;
  /** Apply a change now and save it soon. */
  enqueue: (patch: LayoutPatch) => void;
  /** Save what's pending now. */
  flush: () => void;
}

const isLimitError = (err: unknown) =>
  err instanceof ApiError && err.code === 'LAYOUT_LIMIT_EXCEEDED';

export function useDesktopLayout(holderId: string): DesktopLayoutController {
  const queryClient = useQueryClient();
  const saver = useMemo(
    () =>
      new DesktopLayoutSaver(
        holderId,
        {
          patch: async (holder, body) => {
            const response = await api.patchVaultDesktopLayout({ holder, ...body });
            const saved = { layout: normalizeLayout(response.layout), revision: response.revision };
            // Keep the cache current: a Desktop that mounts again (back from
            // Browse, say) starts from what was last saved, not a stale load.
            // A refetch already in flight would land after this with an older
            // revision, so it is cancelled first.
            const key = queryKeys.vaultDocuments.desktopLayout(holder);
            await queryClient.cancelQueries({ queryKey: key });
            queryClient.setQueryData(key, saved);
            return saved;
          },
        },
        { isLimitError },
      ),
    [holderId, queryClient],
  );

  useEffect(() => {
    saver.activate();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') saver.flush();
    };
    const onUnload = () => saver.flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onUnload);
      saver.dispose();
    };
  }, [saver]);

  const query = useQuery({
    queryKey: queryKeys.vaultDocuments.desktopLayout(holderId),
    queryFn: () => api.getVaultDesktopLayout(holderId),
    staleTime: 30_000,
    retry: 1,
  });
  useEffect(() => {
    if (query.data) saver.setBase(normalizeLayout(query.data.layout), query.data.revision);
  }, [query.data, saver]);

  const snapshot = useSyncExternalStore(saver.subscribe, saver.getSnapshot);
  const enqueue = useCallback((patch: LayoutPatch) => saver.enqueue(patch), [saver]);
  const flush = useCallback(() => saver.flush(), [saver]);

  const ready = query.isSuccess || query.isError;
  const loadFailed = query.isError;
  return useMemo(
    () => ({ layout: snapshot.layout, status: snapshot.status, ready, loadFailed, enqueue, flush }),
    [snapshot, ready, loadFailed, enqueue, flush],
  );
}
